import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { createEmployeeNavSections } from '../components/layout/dashboardNavConfigs';
import ExpenseSummaryCards from '../components/expense/ExpenseSummaryCards';
import ExpenseList from '../components/expense/ExpenseList';
import ExpenseForm from '../components/expense/ExpenseForm';
import AddButton from '../components/common/AddButton';
import { expensesAPI, projectsAPI } from '../services/api';
import {
  Expense,
  ExpenseSummary,
  ExpenseStatus,
  ExpenseCategory,
  EXPENSE_CATEGORIES,
  EXPENSE_STATUSES,
} from '../types/expense';

// ---------- types ----------

interface Project {
  id: number;
  name: string;
}

interface Filters {
  status: string;
  start_date: string;
  end_date: string;
  category: string;
  project_id: string;
}

// ---------- helpers ----------

const EMPTY_SUMMARY: ExpenseSummary = {
  total_count: 0,
  total_amount: '0',
  status_summary: {
    draft: { count: 0, amount: '0' },
    pending: { count: 0, amount: '0' },
    approved: { count: 0, amount: '0' },
    rejected: { count: 0, amount: '0' },
    reimbursed: { count: 0, amount: '0' },
  },
};

function buildSummary(expenses: Expense[]): ExpenseSummary {
  const s: ExpenseSummary = JSON.parse(JSON.stringify(EMPTY_SUMMARY));
  s.total_count = expenses.length;
  let total = 0;
  for (const e of expenses) {
    const amt = parseFloat(e.amount) || 0;
    total += amt;
    const key = e.status as ExpenseStatus;
    if (s.status_summary[key]) {
      s.status_summary[key].count += 1;
      s.status_summary[key].amount = String(
        parseFloat(s.status_summary[key].amount) + amt,
      );
    }
  }
  s.total_amount = String(total);
  return s;
}

// Map Expense (from types/expense.ts) → the shape ExpenseForm expects
function toFormExpense(e: Expense) {
  return {
    id: e.id,
    amount: parseFloat(e.amount),
    currency: e.currency,
    category: e.category as any,
    merchant: e.merchant_name,
    expense_date: e.expense_date,
    project_id: e.project_id ?? null,
    description: e.description,
    billable: e.billable,
    reimbursable: e.reimbursable,
    status: e.status,
    expense_attachments: e.expense_attachments,
  };
}

// ---------- Detail drawer ----------

interface DetailDrawerProps {
  expense: Expense;
  isDark: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSubmit: () => void;
  deleting: boolean;
}

const canMutate = (s: ExpenseStatus) => s === 'draft' || s === 'pending';
const canSubmitExpense = (s: ExpenseStatus) => s === 'draft';

function getStatusBadgeClass(status: ExpenseStatus, isDark: boolean): string {
  const map: Record<ExpenseStatus, string> = {
    draft: isDark
      ? 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
      : 'bg-slate-100 text-slate-600 border border-slate-200',
    pending: isDark
      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
      : 'bg-amber-50 text-amber-700 border border-amber-200',
    approved: isDark
      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
      : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    rejected: isDark
      ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
      : 'bg-rose-50 text-rose-700 border border-rose-200',
    reimbursed: isDark
      ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
      : 'bg-cyan-50 text-cyan-700 border border-cyan-200',
  };
  return map[status] ?? map.draft;
}

const DetailDrawer: React.FC<DetailDrawerProps> = ({
  expense,
  isDark,
  onClose,
  onEdit,
  onDelete,
  onSubmit,
  deleting,
}) => {
  const { t } = useTranslation();
  const statusLabel =
    EXPENSE_STATUSES.find((s) => s.value === expense.status)?.label ??
    expense.status;
  const categoryLabel =
    EXPENSE_CATEGORIES.find((c) => c.value === expense.category)?.label ??
    expense.category;

  const formatAmt = (v: string, cur = 'CAD') =>
    new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: 2,
    }).format(parseFloat(v) || 0);

  const row = (label: string, value: React.ReactNode) => (
    <div
      className={`flex justify-between py-2.5 border-b ${
        isDark ? 'border-slate-700/50' : 'border-slate-100'
      }`}
    >
      <span
        className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
      >
        {value}
      </span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className={`relative w-full max-w-md flex flex-col shadow-2xl ${
          isDark
            ? 'bg-slate-900 border-l border-slate-700/50'
            : 'bg-white border-l border-slate-200'
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${
            isDark ? 'border-slate-700/50' : 'border-slate-200'
          }`}
        >
          <h2
            className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('expense.title') || 'Expense Detail'}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-all ${
              isDark
                ? 'text-slate-400 hover:text-white hover:bg-slate-700'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Amount + status */}
          <div className="mb-6 text-center">
            <div
              className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {formatAmt(expense.amount, expense.currency)}
            </div>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(
                expense.status,
                isDark,
              )}`}
            >
              {statusLabel}
            </span>
          </div>

          {/* Fields */}
          <div>
            {row(
              t('expense.date') || 'Date',
              format(parseISO(expense.expense_date), 'MMMM dd, yyyy'),
            )}
            {row(t('expense.category') || 'Category', categoryLabel)}
            {expense.merchant_name &&
              row(t('expense.merchant') || 'Merchant', expense.merchant_name)}
            {expense.project &&
              row(t('expense.project') || 'Project', expense.project.name)}
            {expense.description &&
              row(
                t('expense.description') || 'Description',
                expense.description,
              )}
            {row(
              t('expense.billable') || 'Billable',
              expense.billable
                ? t('common.yes') || 'Yes'
                : t('common.no') || 'No',
            )}
            {row(
              t('expense.reimbursable') || 'Reimbursable',
              expense.reimbursable
                ? t('common.yes') || 'Yes'
                : t('common.no') || 'No',
            )}
            {expense.approved_by &&
              row(
                t('expense.approvedBy') || 'Approved by',
                expense.approved_by.full_name,
              )}
            {expense.approved_at &&
              row(
                t('expense.approvedAt') || 'Approved at',
                format(parseISO(expense.approved_at), 'MMM dd, yyyy'),
              )}
            {expense.reimbursed_at &&
              row(
                t('expense.reimbursedAt') || 'Reimbursed at',
                format(parseISO(expense.reimbursed_at), 'MMM dd, yyyy'),
              )}
          </div>

          {/* Rejection reason */}
          {expense.status === 'rejected' && expense.rejection_reason && (
            <div
              className={`mt-4 p-4 rounded-xl border ${
                isDark
                  ? 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                  : 'bg-rose-50 border-rose-200 text-rose-700'
              }`}
            >
              <div className="text-xs font-semibold uppercase tracking-wide mb-1">
                {t('expense.rejectionReason') || 'Rejection Reason'}
              </div>
              <p className="text-sm">{expense.rejection_reason}</p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div
          className={`flex flex-wrap gap-2 px-6 py-4 border-t ${
            isDark ? 'border-slate-700/50' : 'border-slate-200'
          }`}
        >
          {canSubmitExpense(expense.status) && (
            <button
              onClick={onSubmit}
              className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-all"
            >
              {t('expense.submitExpense') || 'Submit'}
            </button>
          )}
          {canMutate(expense.status) && (
            <button
              onClick={onEdit}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-all"
            >
              {t('common.edit') || 'Edit'}
            </button>
          )}
          {canMutate(expense.status) && (
            <button
              onClick={onDelete}
              disabled={deleting}
              className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-xl text-sm font-medium hover:bg-rose-700 disabled:opacity-50 transition-all"
            >
              {deleting
                ? t('common.deleting') || 'Deleting...'
                : t('common.delete') || 'Delete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Confirm delete modal ----------

interface ConfirmDeleteProps {
  isDark: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteProps> = ({
  isDark,
  onConfirm,
  onCancel,
  deleting,
}) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div
        className={`relative rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl ${
          isDark
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white border border-slate-200'
        }`}
      >
        <h3
          className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
        >
          {t('expense.deleteExpense') || 'Delete Expense'}
        </h3>
        <p
          className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
        >
          {t('expense.deleteConfirm') ||
            'Are you sure you want to delete this expense? This action cannot be undone.'}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className={`flex-1 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              isDark
                ? 'bg-slate-700 text-white hover:bg-slate-600'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-xl text-sm font-medium hover:bg-rose-700 disabled:opacity-50 transition-all"
          >
            {deleting
              ? t('common.deleting') || 'Deleting...'
              : t('common.delete') || 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Skeleton cards for summary ----------

function SummarySkeletonCards({ isDark }: { isDark: boolean }) {
  const shimmer = isDark ? 'bg-slate-700/50' : 'bg-slate-200';
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className={`rounded-2xl p-5 ${
            isDark
              ? 'bg-slate-900/50 border border-slate-700/50'
              : 'bg-white border border-slate-200 shadow-sm'
          }`}
        >
          <div className={`h-3 w-16 rounded ${shimmer} animate-pulse mb-3`} />
          <div className={`h-7 w-24 rounded ${shimmer} animate-pulse mb-2`} />
          <div className={`h-3 w-12 rounded ${shimmer} animate-pulse`} />
        </div>
      ))}
    </div>
  );
}

// ---------- Filters row ----------

interface FiltersRowProps {
  filters: Filters;
  projects: Project[];
  isDark: boolean;
  onChange: (f: Filters) => void;
}

const FiltersRow: React.FC<FiltersRowProps> = ({
  filters,
  projects,
  isDark,
  onChange,
}) => {
  const { t } = useTranslation();

  const selectClass = `px-3 py-2 rounded-xl border text-sm transition-all focus:outline-none focus:ring-2 focus:ring-violet-500/40 ${
    isDark
      ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500'
      : 'bg-white border-slate-300 text-slate-700 placeholder-slate-400'
  }`;

  const inputClass = `${selectClass} w-full`;

  return (
    <div className="flex flex-wrap gap-3">
      {/* Status */}
      <select
        value={filters.status}
        onChange={(e) => onChange({ ...filters, status: e.target.value })}
        className={selectClass}
      >
        <option value="">
          {t('expense.filters.allStatuses') || 'All Statuses'}
        </option>
        {EXPENSE_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>

      {/* Category */}
      <select
        value={filters.category}
        onChange={(e) => onChange({ ...filters, category: e.target.value })}
        className={selectClass}
      >
        <option value="">
          {t('expense.filters.allCategories') || 'All Categories'}
        </option>
        {EXPENSE_CATEGORIES.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>

      {/* Project */}
      <select
        value={filters.project_id}
        onChange={(e) => onChange({ ...filters, project_id: e.target.value })}
        className={selectClass}
      >
        <option value="">
          {t('expense.filters.allProjects') || 'All Projects'}
        </option>
        {projects.map((p) => (
          <option key={p.id} value={String(p.id)}>
            {p.name}
          </option>
        ))}
      </select>

      {/* Date range */}
      <input
        type="date"
        value={filters.start_date}
        onChange={(e) => onChange({ ...filters, start_date: e.target.value })}
        className={inputClass}
        style={{ width: 'auto' }}
        title={t('expense.filters.startDate') || 'Start date'}
      />
      <input
        type="date"
        value={filters.end_date}
        onChange={(e) => onChange({ ...filters, end_date: e.target.value })}
        className={inputClass}
        style={{ width: 'auto' }}
        title={t('expense.filters.endDate') || 'End date'}
      />
    </div>
  );
};

// ---------- Main page ----------

const MyExpenses: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const [filters, setFilters] = useState<Filters>({
    status: '',
    start_date: '',
    end_date: '',
    category: '',
    project_id: '',
  });

  // Modal / drawer state
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [viewingExpense, setViewingExpense] = useState<Expense | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Expense | null>(null);

  const isActive = (href: string) => {
    if (href === '/employee-dashboard')
      return location.pathname === '/employee-dashboard';
    return location.pathname.startsWith(href);
  };

  const sections = createEmployeeNavSections(t, isActive);

  // Build API params from filters (strip empty values)
  const buildParams = useCallback((f: Filters): Record<string, unknown> => {
    const p: Record<string, unknown> = {};
    if (f.status) p.status = f.status;
    if (f.start_date) p.start_date = f.start_date;
    if (f.end_date) p.end_date = f.end_date;
    if (f.category) p.category = f.category as ExpenseCategory;
    if (f.project_id) p.project_id = Number(f.project_id);
    return p;
  }, []);

  const fetchExpenses = useCallback(
    async (f: Filters) => {
      setLoading(true);
      try {
        const res = await expensesAPI.getMine(buildParams(f));
        const data: Expense[] = res.data?.expenses ?? res.data ?? [];
        setExpenses(data);
      } catch {
        showToast(
          t('expense.messages.fetchFailed') || 'Failed to load expenses',
          'error',
        );
      } finally {
        setLoading(false);
      }
    },
    [buildParams, showToast, t],
  );

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectsAPI.getAll();
      const data: Project[] = res.data?.projects ?? res.data ?? [];
      setProjects(data);
    } catch {
      // Non-critical — filters just won't show project names
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchExpenses(filters);
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleFilterChange = (f: Filters) => {
    setFilters(f);
    fetchExpenses(f);
  };

  const handleSubmitExpense = async (id: number) => {
    try {
      await expensesAPI.submit(id);
      showToast(
        t('expense.messages.submitSuccess') || 'Expense submitted for approval',
        'success',
      );
      fetchExpenses(filters);
      // Close detail drawer if open for this expense
      if (viewingExpense?.id === id) setViewingExpense(null);
    } catch {
      showToast(
        t('expense.messages.submitFailed') || 'Failed to submit expense',
        'error',
      );
    }
  };

  const handleDeleteRequest = (expense: Expense) => {
    setPendingDelete(expense);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await expensesAPI.delete(pendingDelete.id);
      showToast(
        t('expense.messages.deleteSuccess') || 'Expense deleted',
        'success',
      );
      setPendingDelete(null);
      setViewingExpense(null);
      fetchExpenses(filters);
    } catch {
      showToast(
        t('expense.messages.deleteFailed') || 'Failed to delete expense',
        'error',
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleEditFromDrawer = () => {
    if (!viewingExpense) return;
    setEditingExpense(viewingExpense);
    setViewingExpense(null);
    setShowForm(true);
  };

  const handleSubmitFromDrawer = () => {
    if (!viewingExpense) return;
    handleSubmitExpense(viewingExpense.id);
  };

  const handleDeleteFromDrawer = () => {
    if (!viewingExpense) return;
    handleDeleteRequest(viewingExpense);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingExpense(null);
    fetchExpenses(filters);
  };

  const summary = buildSummary(expenses);

  const cardClass = `rounded-2xl border p-6 ${
    isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white border-slate-200'
  }`;

  const content = (
    <>
      {/* Sticky PageHeader - Same as TimeTracking */}
      <div
        className={`border-b ${isDark ? 'bg-slate-900/50 border-slate-700/50' : 'bg-white/80 border-slate-200'} backdrop-blur-sm sticky top-0 z-10`}
      >
        <div className="px-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 py-5">
            <div>
              <h1
                className={`text-xl sm:text-2xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('expense.myExpenses')}
              </h1>
              <p
                className={`mt-0.5 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                {t('expense.myExpensesSubtitle')}
              </p>
            </div>
            <div className="w-full sm:w-auto">
              <AddButton
                label={t('expense.addExpense')}
                onClick={() => {
                  setEditingExpense(null);
                  setShowForm(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        {/* Ambient glow effect for dark mode */}
        {isDark && (
          <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[150px]" />
            <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-600/8 rounded-full blur-[120px]" />
          </div>
        )}
        <div className="p-6">
          {/* Summary cards */}
          {loading ? (
            <SummarySkeletonCards isDark={isDark} />
          ) : (
            <ExpenseSummaryCards summary={summary} />
          )}

          {/* Filters + list card */}
          <div className={cardClass}>
            {/* Filters row */}
            <div
              className={`px-0 py-4 border-b ${
                isDark ? 'border-slate-700' : 'border-slate-200'
              }`}
            >
              <FiltersRow
                filters={filters}
                projects={projects}
                isDark={isDark}
                onChange={handleFilterChange}
              />
            </div>

            {/* Expense list */}
            <ExpenseList
              expenses={expenses}
              loading={loading}
              onAdd={() => {
                setEditingExpense(null);
                setShowForm(true);
              }}
              onEdit={(expense) => {
                setEditingExpense(expense);
                setViewingExpense(null);
                setShowForm(true);
              }}
              onDelete={(id) => {
                const e = expenses.find((x) => x.id === id);
                if (e) handleDeleteRequest(e);
              }}
              onSubmit={handleSubmitExpense}
              onViewDetail={(expense) => setViewingExpense(expense)}
            />
          </div>
          {/* Expense form modal */}
          <ExpenseForm
            isOpen={showForm}
            onClose={() => {
              setShowForm(false);
              setEditingExpense(null);
            }}
            onSuccess={handleFormSuccess}
            expenseToEdit={
              editingExpense ? toFormExpense(editingExpense) : null
            }
            projects={projects}
          />

          {/* Detail drawer */}
          {viewingExpense && (
            <DetailDrawer
              expense={viewingExpense}
              isDark={isDark}
              onClose={() => setViewingExpense(null)}
              onEdit={handleEditFromDrawer}
              onDelete={handleDeleteFromDrawer}
              onSubmit={handleSubmitFromDrawer}
              deleting={deleting}
            />
          )}

          {/* Confirm delete modal */}
          {pendingDelete && (
            <ConfirmDeleteModal
              isDark={isDark}
              onConfirm={handleDeleteConfirm}
              onCancel={() => setPendingDelete(null)}
              deleting={deleting}
            />
          )}
        </div>
      </div>
    </>
  );

  return <DashboardLayout sections={sections}>{content}</DashboardLayout>;
};

export default MyExpenses;
